import axios from "axios"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { decodeToken } from "react-jwt"

import { Link as RouterLink } from "react-router-dom"
import FormControl from "@material-ui/core/FormControl"
import Button from "@material-ui/core/Button"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"

import { AxiosHelper } from "common/helpers"
import { AuthApi } from "common/api"
import { useQuery } from "common/hooks"
import { AuthLayout } from "./AuthLayout"
import { PasswordTextFieldLogin } from "components/partials"
import { Alert } from "components/widgets"

const ResetPassword = () => {
    const query = useQuery()
    const token = query.get("token")
    const hasToken = token && token.length
    const [isSuccess, setIsSuccess] = useState(false)

    function onSubmitted() {
        setIsSuccess(true)
    }

    if (!hasToken) {
        return <ErrorForm />
    }

    if (isSuccess) {
        return <SuccessForm />
    }

    return <Form token={token} onSubmitted={onSubmitted} />
}

const Form = props => {
    const { t } = useTranslation()
    const { token, onSubmitted } = props
    const decoded = decodeToken(token)

    const [formData, setFormData] = useState({ password: "" })
    const [error, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isValidPassword, setIsValidPassword] = useState(false)
    const [fontSize, setFontSize] = useState(window.innerWidth * 0.015)

    useEffect(() => {
        const handleResize = () => {
            setFontSize(window.innerWidth * 0.015);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [])

    const styleButton = {
        backgroundColor: 'black',
        color: 'white',
        fontWeight: 'bold',
        fontSize: fontSize * 0.75
    }

    async function onSubmit(event) {
        event.preventDefault()

        if (!isValidPassword) return

        setIsLoading(true)

        try {
            await AuthApi.resetPassword({ token, password: formData.password })
            onSubmitted()
        } catch (error) {
            // must be in catch, because when the request was successful this form is gone and the call would throw an error
            setIsLoading(false)

            if (axios.isAxiosError(error)) {
                const parsed = AxiosHelper.parseError(error)
                setError(typeof parsed === "string" ? { general: parsed } : parsed)
            } else {
                setError({ general: t("errors.somethingWentWrong") })
            }
        }
    }

    function onValidated(hasError) {
        setIsValidPassword(hasError === false)
    }

    const onChange = key => event => {
        setFormData({ ...formData, [key]: event.target.value })
    }

    return (
        <AuthLayout>
            {error?.general && <Alert severity="error">{error.general}</Alert>}

            <Box width="100%" component="form" onSubmit={onSubmit}>
                {/* Password forms should have (optionally hidden) username fields for accessibility */}
                <input hidden autoComplete="username" defaultValue={decoded?.email} />

                <FormControl fullWidth margin="normal">
                    <PasswordTextFieldLogin
                        required
                        label={t("view.resetPassword.passwordLabel")}
                        value={formData.password}
                        onChange={onChange("password")}
                        autoComplete="new-password"
                        error={Boolean(error?.password)}
                        helperText={error?.password}
                        CheckListProps={{
                            show: true,
                            onChange: onValidated
                        }}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <Button
                        style={styleButton}
                        fullWidth
                        disabled={isLoading || !isValidPassword}
                        color="primary"
                        variant="contained"
                        type="submit"
                    >
                        {isLoading
                            ? t("view.resetPassword.submitButtonLoading")
                            : t("view.resetPassword.submitButton")}
                    </Button>
                </FormControl>
            </Box>
        </AuthLayout>
    )
}

const SuccessForm = () => {
    const { t } = useTranslation()

    const styleButton = {
        backgroundColor: 'black',
        color: 'white',
        fontWeight: 'bold',
    }

    return (
        <AuthLayout>
            <FormControl fullWidth margin="normal">
                <Typography style={{ color: "white" }} component="h2" variant="h6">
                    {t("view.resetPassword.success.title")}
                </Typography>

                <Typography style={{ color: "white" }}>
                    {t("view.resetPassword.success.info")}
                </Typography>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <Button
                    style={styleButton}
                    fullWidth
                    component={RouterLink}
                    to="/login"
                    color="primary"
                    variant="contained"
                >
                    {t("general.login")}
                </Button>
            </FormControl>
        </AuthLayout>
    )
}

const ErrorForm = () => {
    const { t } = useTranslation()

    return (
        <AuthLayout>
            <FormControl fullWidth margin="normal">
                <Typography style={{ color: "white" }} component="h2" variant="h6">
                    {t("view.resetPassword.noToken.title")}
                </Typography>
            </FormControl>
            <FormControl>
                <Typography style={{ color: "white" }}>
                    {t("view.resetPassword.noToken.info")}
                </Typography>
            </FormControl>
        </AuthLayout>
    )
}

export default ResetPassword
